import { useEffect, useState } from 'react'
import { QrCode, RotateCcw, Truck } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import type {
    SalesOrderStatus,
    StockOutItemInput,
    StockOutMaterialItemInput,
} from '@/lib/api/sales-order-api'
import type { DeliverySettlement } from '@/lib/delivery-settlement'
import type {
    RefundFieldErrors,
    RefundFormValue,
} from '@/lib/schemas/refund-schemas'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    useCancelOrder,
    useCarrierMarkDelivered,
    useCarrierMarkDeliveryFailed,
    useCarrierMarkInDelivery,
    useCarrierMarkReadyToDeliver,
    useConfirmDelivery,
    useConfirmOrder,
    useConfirmReturn,
    useFailDelivery,
    usePickupOrder,
    useRecreateOrder,
    useSalesOrder,
    useStartDelivery,
    useStockCheck,
    useStockOutOrder,
} from '@/hooks/use-sales-order-query'
import { useAuth } from '@/hooks/use-auth'
import { QRScannerDialog } from '@/components/sections/qr-scanner-dialog'
import { ConfirmReturnDialog } from '@/components/sections/confirm-return-dialog'
import { SalesReturnDialog } from '@/components/sections/sales-return-dialog'
import { RefundSection } from '@/components/sections/refund-section'
import {
    emptyRefund,
    refundFieldErrors,
    toRefundChoice,
} from '@/lib/schemas/refund-schemas'
import { StartDeliveryDialog } from '@/components/delivery/start-delivery-dialog'
import { DeliverySettlementDialog } from '@/components/delivery/delivery-settlement-dialog'
import { AssignDriverDialog } from '@/components/delivery/assign-driver-dialog'
import { StockOutDialog } from '@/components/sections/stock-out-dialog'
import { useDuplicateOrderAction } from '@/components/sections/order-duplicate-action'
import { m } from '@/lib/paraglide/messages'
import { CarrierDispatchDialog } from '@/components/carrier/carrier-dispatch-dialog'

export type CarrierShipmentReadState =
    'disabled' | 'loading' | 'error' | 'none' | 'active'

interface Props {
    orderId: string
    status: SalesOrderStatus
    onActionComplete?: () => void
    /**
     * The parcel is already booked with a shipping carrier. It never passes
     * through one of our drivers and the backend rejects every driver
     * transition on it, so those buttons are hidden rather than left to fail.
     */
    viaCarrier?: boolean
    /** Fail-safe state of the carrier shipment lookup on the order detail. */
    carrierShipmentState?: CarrierShipmentReadState
}

/**
 * Dialog state for actions requiring a note.
 * - cancel / fail_delivery: existing driver flow, note >= 3 chars.
 * - carrier_delivered / carrier_delivery_failed: kế toán reconcile — bắt buộc
 *   ghi note để audit (VD "AWB 123, giao 14h ngày X", "khách từ chối vì Y").
 *   Không có nút "OK không note" để tránh mất audit trail. Hoàn hàng của đơn
 *   hãng ngoài đi qua "Xác nhận hoàn" như mọi đơn (M-38).
 */
type NoteDialogAction =
    'cancel' | 'fail_delivery' | 'carrier_delivered' | 'carrier_delivery_failed'

type NoteDialogState =
    { open: false } | { open: true; action: NoteDialogAction }

function getNoteDialogConfig(action: NoteDialogAction) {
    return {
        cancel: {
            title: m.order_cancel_title(),
            description: m.order_cancel_desc(),
            placeholder: m.order_cancel_placeholder(),
        },
        fail_delivery: {
            title: m.order_fail_delivery_title(),
            description: m.order_fail_delivery_desc(),
            placeholder: m.order_fail_delivery_placeholder(),
        },
        carrier_delivered: {
            title: m.order_carrier_delivered_title(),
            description: m.order_carrier_delivered_desc(),
            placeholder: m.order_carrier_delivered_placeholder(),
        },
        carrier_delivery_failed: {
            title: m.order_carrier_delivery_failed_title(),
            description: m.order_carrier_delivery_failed_desc(),
            placeholder: m.order_carrier_delivery_failed_placeholder(),
        },
    }[action]
}

/**
 * Một hành động của đơn, mô tả dưới dạng dữ liệu thay vì JSX.
 *
 * Tách descriptor ra khỏi JSX để cùng một danh sách render được ở 2 chỗ: hàng
 * nút ngang (desktop) và menu kebab (mobile). `primary` đánh dấu hành động
 * chính của trạng thái hiện tại — mobile giữ nó ngoài menu.
 */
export interface OrderActionItem {
    key: string
    label: string
    icon?: ReactNode
    variant?: 'default' | 'outline' | 'destructive'
    disabled?: boolean
    /**
     * Hiện mờ nhưng VẪN bấm được: hành động bị chặn mà người dùng cần bấm để
     * đọc lý do (nút disabled không nhận hover/chạm, điện thoại không có
     * tooltip). `onSelect` của mục này chỉ báo lý do, không làm gì khác.
     */
    muted?: boolean
    /** Tooltip giải thích vì sao nút đang bị khoá. */
    title?: string
    primary?: boolean
    onSelect: () => void
}

export interface OrderWorkflowActions {
    items: OrderActionItem[]
    /** Toàn bộ dialog của các hành động. Phải render NGOÀI menu kebab. */
    dialogs: ReactNode
}

export function useOrderWorkflowActions({
    orderId,
    status,
    onActionComplete,
    viaCarrier = false,
    carrierShipmentState,
}: Props): OrderWorkflowActions {
    const [noteDialog, setNoteDialog] = useState<NoteDialogState>({
        open: false,
    })
    const [noteText, setNoteText] = useState('')
    const [qrScanOpen, setQrScanOpen] = useState(false)
    const [startDeliveryOpen, setStartDeliveryOpen] = useState(false)
    const [carrierDispatchOpen, setCarrierDispatchOpen] = useState(false)
    const [assignDriverOpen, setAssignDriverOpen] = useState(false)
    const [settlementOpen, setSettlementOpen] = useState(false)
    // Popup "Tạo phiếu xuất kho" — nhân viên chọn lô trước khi xuất.
    const [stockOutOpen, setStockOutOpen] = useState(false)
    const stockOutWorkflow = useWorkflowTracking('order_stock_out', {
        active: stockOutOpen,
        attemptKey: orderId,
    })
    const [, setPending] = useState<{
        items: StockOutItemInput[]
        materialItems: StockOutMaterialItemInput[]
    } | null>(null)
    const [returnDialogOpen, setReturnDialogOpen] = useState(false)
    // M-44: trả hàng đơn đã giao + lựa chọn hoàn tiền khi huỷ đơn.
    const [salesReturnOpen, setSalesReturnOpen] = useState(false)
    const [cancelRefund, setCancelRefund] =
        useState<RefundFormValue>(emptyRefund)
    const [cancelRefundErrors, setCancelRefundErrors] =
        useState<RefundFieldErrors>({})
    const [recreateOpen, setRecreateOpen] = useState(false)
    const { data: order } = useSalesOrder(orderId)
    // M-76: tách lần xuất kho đơn quầy / KiotViet / đơn thường trong cùng
    // workflow `order_stock_out` (đơn "Bán ngay" giờ chờ thủ kho xuất).
    const stockOutSource: 'counter' | 'kiotviet' | 'standard' =
        order?.is_quick_sale
            ? 'counter'
            : order?.source_channel === 'kiotviet'
              ? 'kiotviet'
              : 'standard'
    useEffect(() => {
        if (stockOutOpen)
            stockOutWorkflow.setContext({ orderSource: stockOutSource })
    }, [stockOutOpen, stockOutSource, stockOutWorkflow])
    const carrierQuoteContext = order
        ? {
              shippingAddress: order.shipping_address,
              shippingProvince: order.shipping_province,
              shippingProvinceCode: order.shipping_province_code,
              shippingWard: order.shipping_ward,
              shippingWardCode: order.shipping_ward_code,
              totalAmount: order.total_amount,
              paidAmount: order.paid_amount,
              collectCod: order.collect_cod,
          }
        : null

    const { hasPermission, user } = useAuth()
    const isDriver =
        hasPermission('sales_orders.pickup_order') &&
        !hasPermission('sales_orders.assign_driver')
    // Admin / kế toán không tự đi giao: họ nhận đơn thay tài xế qua popup chọn
    // người. `isDriver` đã loại nhóm này nên không tài khoản nào thấy cả nút
    // tự nhận lẫn nút gán.
    const canAssignDriver = hasPermission('sales_orders.assign_driver')

    // Gates every driver-only transition: those are exactly the ones the
    // backend refuses once a shipping carrier holds the parcel.
    const effectiveCarrierState =
        carrierShipmentState ?? (viaCarrier ? 'active' : 'disabled')
    const internalDelivery =
        effectiveCarrierState === 'disabled' || effectiveCarrierState === 'none'

    // Each action maps to the permission its backend endpoint enforces, so a
    // user only ever sees a button they can actually use. "Xuất kho" guards on
    // stock_out.confirm.
    const canConfirm = hasPermission('sales_orders.confirm')
    const canCancel = hasPermission('sales_orders.cancel')
    const canStockOut = hasPermission('stock_out.confirm')
    // Permission thôi chưa đủ cho 3 bước giao hàng: backend còn buộc người bấm
    // hoặc là tài xế ĐƯỢC GÁN, hoặc giữ `delivery_trips.force_end` để bấm hộ
    // (nhiều bác tài không có điện thoại nên văn phòng bấm thay). Xem
    // canActForDriver bên usecase — đây là bản sao đúng luật đó.
    // Đơn chưa gán tài xế thì không ai bấm hộ được: chuyến giao và sổ tiền COD
    // đều ghi theo tài xế được gán.
    const isAssignedDriver = Boolean(
        user?.id && order?.driver_assignee_id === user.id,
    )
    const canActForDriver =
        Boolean(order?.driver_assignee_id) &&
        (isAssignedDriver || hasPermission('delivery_trips.force_end'))
    const canStartDelivery =
        hasPermission('sales_orders.start_delivery') && canActForDriver
    const canDispatchCarrier =
        carrierQuoteContext !== null &&
        hasPermission('carriers.dispatch') &&
        effectiveCarrierState === 'none' &&
        !order?.driver_assignee_id &&
        (status === 'stock_out' || status === 'ready_to_deliver')
    const canConfirmDelivery =
        hasPermission('sales_orders.confirm_delivery') && canActForDriver
    const canFailDelivery =
        hasPermission('sales_orders.fail_delivery') && canActForDriver
    const canConfirmReturn = hasPermission('sales_orders.confirm_return')
    // Trả hàng đơn đã giao (M-44): mọi đơn đã giao trừ đơn tạo từ hóa đơn
    // KiotViet (trả hàng bên KiotViet). Hết hàng để trả thì popup tự báo.
    const canReturnDelivered =
        hasPermission('sales_orders.return_delivered') &&
        status === 'delivered' &&
        order?.source_channel !== 'kiotviet' &&
        // Đã trả hết giá trị đơn thì không còn gì để trả.
        (order?.returned_amount ?? 0) < (order?.total_amount ?? 0)
    // Kế toán reconcile — chỉ hiện khi có permission + đơn giao bởi carrier
    // ngoài (Viettelpost, GHTK, GHN…). Không phụ thuộc role driver — 1
    // account có thể có cả 2 permission thì thấy cả 2 bộ button.
    const canCarrierReconcile = hasPermission('sales_orders.carrier_reconcile')
    const showCarrierReconcile =
        canCarrierReconcile && order?.shipping_carrier_is_external === true

    // Backend nhận gán tài xế ở stock_out và cả ready_to_deliver khi đơn chưa
    // ai nhận (xác nhận đóng gói có thể đẩy đơn sang ready_to_deliver trước).
    // Đơn đã có tài xế thì không gán đè — backend cũng chặn bằng claim atomic.
    // Đơn KiotViet là bán tại quầy — khách nhận hàng ngay, KHÔNG cần giao. Ẩn nút
    // gán tài xế cho các đơn này (BE cũng đã loại chúng khỏi danh sách chờ nhận).
    const showAssignDriver =
        canAssignDriver &&
        internalDelivery &&
        order?.source_channel !== 'kiotviet' &&
        (status === 'stock_out' ||
            (status === 'ready_to_deliver' && !order?.driver_assignee_id))

    // Huỷ đơn cho phép tới hết ready_to_deliver (đơn chưa rời kho đi giao) và
    // chỉ với kho/admin (non-driver). Backend hoàn tồn + vật tư + huỷ công nợ,
    // và clear gán tài xế nếu đơn đã được nhận. Từ in_delivery trở đi phải đi
    // qua luồng giao thất bại → hoàn hàng, không huỷ trực tiếp.
    // Ngoại lệ: đơn "Bán ngay" tại quầy (is_quick_sale) tự nhảy delivered ngay
    // sau khi bán — khách đổi ý tại quầy vẫn huỷ được (BE hoàn kho + dọn nháp HĐĐT).
    const canShowCancel =
        canCancel &&
        !isDriver &&
        internalDelivery &&
        (status === 'draft' ||
            status === 'new' ||
            status === 'awaiting_packaging' ||
            status === 'confirmed' ||
            status === 'stock_out' ||
            status === 'ready_to_deliver' ||
            (status === 'delivered' &&
                Boolean(order?.is_quick_sale) &&
                // Đã có phiếu trả hàng (M-44) thì BE chặn huỷ; phần còn
                // lại đi tiếp bằng "Trả hàng", không mời huỷ + hoàn cả đơn.
                (order?.returned_amount ?? 0) <= 0))

    // "Huỷ và tạo lại" — khách đổi số lượng ngay sau khi xác nhận. Chỉ ở
    // `confirmed`: trước đó sửa thẳng được, sau đó hàng đã rời kho. Cùng nhóm
    // điều kiện HIỂN THỊ với nút Huỷ (quyền huỷ, không phải tài xế, đơn không đi
    // nhà xe ngoài) vì bản chất thao tác vẫn là một lần huỷ đơn.
    const canShowRecreate =
        canCancel && !isDriver && internalDelivery && status === 'confirmed'
    // Không đủ điều kiện thì VẪN hiện nút, chỉ mờ đi kèm lý do — người dùng phải
    // biết vì sao không bấm được. (Rule "không disable nút" trong AGENTS.md áp
    // cho nút SUBMIT của form, không áp cho action button theo trạng thái đơn.)
    const recreateBlockedReason =
        order && order.payment_status !== 'unpaid'
            ? m.order_recreate_disabled_paid()
            : order?.is_quick_sale
              ? m.order_recreate_disabled_quick_sale()
              : order?.gift
                ? m.order_recreate_disabled_gift()
                : !hasPermission('sales_orders.create')
                  ? m.order_recreate_disabled_no_create_permission()
                  : undefined

    // Nút "Xác nhận đơn" ở trạng thái awaiting_packaging phải đọc /stock-check
    // để khoá khi kho vẫn thiếu. TanStack Query cache share với
    // AwaitingPackagingPanel qua cùng query key `orderKeys.stockCheck(orderId)`
    // — không gây double fetch.
    const { data: stockCheck, isLoading: stockCheckLoading } = useStockCheck(
        orderId,
        status === 'awaiting_packaging',
    )
    const awaitingHasData = stockCheck !== undefined
    // Ready derive từ shortfalls: khớp logic Panel, chống mâu thuẫn BE.
    const awaitingShortStock =
        awaitingHasData && (stockCheck.shortfalls?.length ?? 0) > 0
    // Trước khi có kết quả check → coi như chưa ready (tránh cashier lỡ tay bấm
    // Confirm ngay giây đầu, BE lock request tới khi stock đủ mới cho qua).
    const awaitingNotReady = !awaitingHasData || awaitingShortStock

    const confirm = useConfirmOrder()
    const stockOut = useStockOutOrder()
    const pickupOrder = usePickupOrder()
    const startDelivery = useStartDelivery()
    const confirmDelivery = useConfirmDelivery()
    const failDelivery = useFailDelivery()
    const confirmReturn = useConfirmReturn()
    const cancelOrder = useCancelOrder()
    const recreateOrder = useRecreateOrder()
    const duplicateAction = useDuplicateOrderAction({ orderId, order })
    // Carrier reconcile mutations — kế toán mark trạng thái đơn của carrier
    // ngoài. Payload = note optional; không có geo / vehicle / COD.
    const carrierReady = useCarrierMarkReadyToDeliver()
    const carrierInDelivery = useCarrierMarkInDelivery()
    const carrierDelivered = useCarrierMarkDelivered()
    const carrierFailed = useCarrierMarkDeliveryFailed()

    const isPending =
        confirm.isPending ||
        stockOut.isPending ||
        pickupOrder.isPending ||
        startDelivery.isPending ||
        confirmDelivery.isPending ||
        failDelivery.isPending ||
        confirmReturn.isPending ||
        cancelOrder.isPending ||
        recreateOrder.isPending ||
        duplicateAction.isPending ||
        carrierReady.isPending ||
        carrierInDelivery.isPending ||
        carrierDelivered.isPending ||
        carrierFailed.isPending

    const awaitingConfirmDisabled =
        isPending || stockCheckLoading || awaitingNotReady

    const run = async (fn: () => Promise<unknown>) => {
        try {
            await fn()
            onActionComplete?.()
        } catch {
            // errors handled in hooks via toast
        }
    }

    const navigate = useNavigate()

    // Huỷ đơn cũ + tạo đơn nháp mới là MỘT lệnh backend (cùng transaction), nên
    // ở đây chỉ còn việc mở màn sửa của đơn vừa sinh ra.
    const submitRecreate = () => {
        setRecreateOpen(false)
        void run(async () => {
            const created = await recreateOrder.mutateAsync(orderId)
            const newOrderId = created.data?.id
            if (!newOrderId) return
            void navigate({
                to: '/orders/$orderId',
                params: { orderId: newOrderId },
                search: { edit: true },
            })
        })
    }

    const openNoteDialog = (action: NoteDialogAction) => {
        setNoteText('')
        setCancelRefund(emptyRefund())
        setCancelRefundErrors({})
        setNoteDialog({ open: true, action })
    }

    // Huỷ đơn đã trừ kho (có nợ đơn) mà khách đã trả tiền → chọn hoàn tiền.
    // Hoàn tối đa = tiền đã thu ròng của đơn.
    const cancelRefundable =
        status === 'confirmed' ||
        status === 'stock_out' ||
        status === 'ready_to_deliver' ||
        (status === 'delivered' && Boolean(order?.is_quick_sale))
            ? (order?.paid_amount ?? 0)
            : 0

    const closeNoteDialog = () => setNoteDialog({ open: false })

    const submitNoteDialog = () => {
        if (!noteDialog.open || noteText.trim().length < 3) return
        const note = noteText.trim()
        const action = noteDialog.action
        if (action === 'cancel' && cancelRefundable > 0) {
            const errs = refundFieldErrors(cancelRefund, cancelRefundable)
            setCancelRefundErrors(errs)
            if (Object.keys(errs).length > 0) return
        }
        closeNoteDialog()

        switch (action) {
            case 'cancel':
                void run(async () => {
                    await cancelOrder.mutateAsync({
                        id: orderId,
                        data: {
                            note,
                            refund:
                                cancelRefundable > 0
                                    ? toRefundChoice(cancelRefund)
                                    : undefined,
                        },
                    })
                })
                return
            case 'fail_delivery':
                void run(async () => {
                    await failDelivery.mutateAsync({
                        id: orderId,
                        data: { note },
                    })
                })
                return
            case 'carrier_delivered':
                void run(async () => {
                    await carrierDelivered.mutateAsync({
                        id: orderId,
                        data: { note },
                    })
                })
                return
            case 'carrier_delivery_failed':
                void run(async () => {
                    await carrierFailed.mutateAsync({
                        id: orderId,
                        data: { note },
                    })
                })
                return
        }
    }

    // Trả về promise của request để popup chọn cách thu đọc được kết quả.
    const submitSettlement = (settlement: DeliverySettlement) => {
        setSettlementOpen(false)
        const confirmed = confirmDelivery.mutateAsync({
            id: orderId,
            ...settlement,
        })
        void run(() => confirmed)
        return confirmed
    }

    // Confirm chỉ chốt đơn (đổi trạng thái + ghi công nợ), KHÔNG trừ tồn kho
    // → không cần kiểm tra thiếu vật tư ở đây. Việc trừ tồn SP + vật tư dời
    // sang bước "Xuất kho".
    const handleConfirmClick = () => {
        proceedConfirm(false)
    }

    // Xuất kho là bước trừ tồn SP + vật tư thật (tạo PXK/PXKBB approved). Check
    // draft vật tư trước khi xuất:
    //  1. Load draft đã lưu lúc Create.
    //  2. Nếu draft có row qty > available → show warning popup.
    //  3. User accept → xuất kho (BE vẫn xuất phần có + audit shortage).
    // Submit từ popup chọn lô: giữ cảnh báo thiếu vật tư đóng gói (vật tư trừ tự
    // động ở BE) rồi mới xuất kho với lô đã chọn.
    const handleStockOutSubmit = (
        items: StockOutItemInput[],
        materialItems: StockOutMaterialItemInput[],
    ) => {
        // Thiếu vật tư deductible đã bị chặn ngay trong popup (chọn lô strict) +
        // BE strict theo lô chỉ định → không cần pre-check draft. Xuất kho thẳng.
        setPending({ items, materialItems })
        proceedStockOut(items, materialItems)
    }

    const proceedStockOut = (
        items: StockOutItemInput[],
        materialItems: StockOutMaterialItemInput[],
    ) => {
        void run(async () => {
            await stockOutWorkflow.run(
                () =>
                    stockOut.mutateAsync({ id: orderId, items, materialItems }),
                (result) => result.data?.id,
            )
            setStockOutOpen(false)
            setPending(null)
        })
    }

    const proceedConfirm = (acceptShortage: boolean) => {
        // Hệ thống 1 kho + FIFO tự động qua product_lots adapter — mọi channel
        // đều đi thẳng confirm, không còn lot picker thủ công.
        void run(async () => {
            await confirm.mutateAsync({
                id: orderId,
                data: {
                    accept_packaging_shortage: acceptShortage || undefined,
                },
            })
        })
    }

    const dialogConfig = noteDialog.open
        ? getNoteDialogConfig(noteDialog.action)
        : null

    const actionItems: OrderActionItem[] = []

    if ((status === 'new' || status === 'draft') && canConfirm) {
        actionItems.push({
            key: 'confirm',
            label: m.order_action_confirm(),
            disabled: isPending,
            primary: true,
            onSelect: () => handleConfirmClick(),
        })
    }
    if (status === 'awaiting_packaging' && canConfirm) {
        actionItems.push({
            key: 'awaiting-confirm',
            label: m.await_confirm_button(),
            disabled: awaitingConfirmDisabled,
            title:
                awaitingNotReady && awaitingHasData
                    ? m.await_confirm_disabled_hint()
                    : undefined,
            primary: true,
            onSelect: () => handleConfirmClick(),
        })
    }
    if (status === 'confirmed' && canStockOut) {
        actionItems.push({
            key: 'create-pxk',
            label: m.order_action_create_pxk(),
            disabled: isPending,
            primary: true,
            onSelect: () => setStockOutOpen(true),
        })
    }
    if (status === 'stock_out' && isDriver && internalDelivery) {
        actionItems.push(
            {
                key: 'pickup',
                label: m.order_action_pickup(),
                disabled: isPending,
                primary: true,
                onSelect: () =>
                    void run(() => pickupOrder.mutateAsync({ id: orderId })),
            },
            {
                key: 'pickup-qr',
                label: m.order_action_pickup_qr(),
                icon: <QrCode className="size-4" />,
                variant: 'outline',
                onSelect: () => setQrScanOpen(true),
            },
        )
    }
    if (showAssignDriver) {
        actionItems.push({
            key: 'assign-driver',
            label: m.order_action_assign_driver(),
            disabled: isPending,
            primary: true,
            onSelect: () => setAssignDriverOpen(true),
        })
    }
    // Carrier reconcile — kế toán bàn giao đơn của carrier ngoài
    // (Viettelpost, GHTK, GHN…) sang trạng thái ready_to_deliver.
    // Bỏ qua bước gán driver / claim vì driver in-house không đụng
    // tới các đơn này.
    if (status === 'stock_out' && showCarrierReconcile) {
        actionItems.push({
            key: 'carrier-ready',
            label: m.order_action_carrier_ready_to_deliver(),
            disabled: isPending,
            primary: true,
            onSelect: () =>
                void run(() => carrierReady.mutateAsync({ id: orderId })),
        })
    }
    if (status === 'ready_to_deliver' && canStartDelivery && internalDelivery) {
        actionItems.push({
            key: 'start-delivery',
            label: m.order_action_start_delivery(),
            disabled: isPending,
            primary: true,
            onSelect: () => setStartDeliveryOpen(true),
        })
    }
    if (canDispatchCarrier) {
        actionItems.push({
            key: 'carrier-dispatch',
            label: m.carrier_form_title(),
            icon: <Truck className="size-4" />,
            variant: 'outline',
            onSelect: () => setCarrierDispatchOpen(true),
        })
    }
    if (status === 'ready_to_deliver' && showCarrierReconcile) {
        actionItems.push({
            key: 'carrier-in-delivery',
            label: m.order_action_carrier_in_delivery(),
            disabled: isPending,
            primary: true,
            onSelect: () =>
                void run(() => carrierInDelivery.mutateAsync({ id: orderId })),
        })
    }
    if (status === 'in_delivery') {
        if (canConfirmDelivery && internalDelivery) {
            actionItems.push({
                key: 'confirm-delivery',
                label: m.order_action_confirm_delivery(),
                disabled: isPending,
                primary: true,
                onSelect: () => setSettlementOpen(true),
            })
        }
        if (canFailDelivery && internalDelivery) {
            actionItems.push({
                key: 'fail-delivery',
                label: m.order_action_fail_delivery(),
                variant: 'destructive',
                disabled: isPending,
                onSelect: () => openNoteDialog('fail_delivery'),
            })
        }
        if (showCarrierReconcile) {
            actionItems.push(
                {
                    key: 'carrier-delivered',
                    label: m.order_action_carrier_delivered(),
                    disabled: isPending,
                    primary: true,
                    onSelect: () => openNoteDialog('carrier_delivered'),
                },
                {
                    key: 'carrier-delivery-failed',
                    label: m.order_action_carrier_delivery_failed(),
                    variant: 'destructive',
                    disabled: isPending,
                    onSelect: () => openNoteDialog('carrier_delivery_failed'),
                },
            )
        }
    }
    // Mọi đơn giao thất bại — kể cả đơn hãng ngoài/VTP — chỉ hoàn qua đây
    // (M-38): webhook hãng không tự hoàn kho, người xác nhận mới hoàn.
    if (status === 'delivery_failed' && canConfirmReturn) {
        actionItems.push({
            key: 'confirm-return',
            label: m.order_action_confirm_return(),
            disabled: isPending,
            primary: true,
            onSelect: () => setReturnDialogOpen(true),
        })
    }
    if (canReturnDelivered) {
        actionItems.push({
            key: 'sales-return',
            label: m.order_action_sales_return(),
            variant: 'outline',
            disabled: isPending,
            onSelect: () => setSalesReturnOpen(true),
        })
    }
    // Nút Huỷ dùng chung cho mọi trạng thái huỷ được (xem canShowCancel).
    // Gộp về 1 chỗ để tránh lặp nhánh theo status.
    if (canShowCancel) {
        actionItems.push({
            key: 'cancel',
            label: m.order_action_cancel(),
            variant: 'destructive',
            disabled: isPending,
            onSelect: () => openNoteDialog('cancel'),
        })
    }
    if (canShowRecreate) {
        actionItems.push({
            key: 'recreate',
            label: m.order_recreate_btn(),
            icon: <RotateCcw className="size-4" />,
            variant: 'outline',
            disabled: Boolean(recreateBlockedReason) || isPending,
            title: recreateBlockedReason,
            onSelect: () => setRecreateOpen(true),
        })
    }
    // Sao chép đơn (M-187): mọi trạng thái, xem useDuplicateOrderAction.
    if (duplicateAction.item) {
        actionItems.push({
            ...duplicateAction.item,
            disabled: duplicateAction.item.disabled || isPending,
        })
    }

    const dialogs = (
        <>
            {carrierQuoteContext && (
                <CarrierDispatchDialog
                    open={carrierDispatchOpen}
                    onOpenChange={setCarrierDispatchOpen}
                    orderId={orderId}
                    quoteContext={carrierQuoteContext}
                />
            )}

            <QRScannerDialog
                open={qrScanOpen}
                onOpenChange={setQrScanOpen}
                mode="pickup"
                onComplete={onActionComplete}
            />

            <StartDeliveryDialog
                open={startDeliveryOpen}
                onOpenChange={(open) => {
                    setStartDeliveryOpen(open)
                    if (!open) onActionComplete?.()
                }}
                orderId={orderId}
                driverId={order?.driver_assignee_id}
            />

            <AssignDriverDialog
                open={assignDriverOpen}
                onOpenChange={(open) => {
                    setAssignDriverOpen(open)
                    if (!open) onActionComplete?.()
                }}
                orderId={orderId}
            />

            {status === 'confirmed' && canStockOut && (
                <StockOutDialog
                    workflow={stockOutWorkflow}
                    orderId={orderId}
                    open={stockOutOpen}
                    onOpenChange={setStockOutOpen}
                    onSubmit={(items, materialItems) =>
                        void handleStockOutSubmit(items, materialItems)
                    }
                    submitting={isPending}
                />
            )}

            <ConfirmReturnDialog
                open={returnDialogOpen}
                orderId={orderId}
                onClose={() => setReturnDialogOpen(false)}
                onSuccess={onActionComplete}
            />

            {canReturnDelivered && (
                <SalesReturnDialog
                    open={salesReturnOpen}
                    orderId={orderId}
                    onClose={() => setSalesReturnOpen(false)}
                    onSuccess={onActionComplete}
                />
            )}

            {/* Xác nhận "Huỷ và tạo lại". Lý do huỷ do backend ghi cố định
                ("Huỷ để tạo lại") nên hộp thoại này không cần ô nhập ghi chú. */}
            <Dialog open={recreateOpen} onOpenChange={setRecreateOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {m.order_recreate_confirm_title()}
                        </DialogTitle>
                        <DialogDescription>
                            {m.order_recreate_confirm_desc()}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setRecreateOpen(false)}
                        >
                            {m.common_close()}
                        </Button>
                        <Button onClick={submitRecreate} disabled={isPending}>
                            {m.order_recreate_confirm_submit()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Note dialog for cancel / fail delivery */}
            <Dialog
                open={noteDialog.open}
                onOpenChange={(open) => !open && closeNoteDialog()}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{dialogConfig?.title}</DialogTitle>
                        <DialogDescription>
                            {dialogConfig?.description}
                        </DialogDescription>
                    </DialogHeader>
                    <textarea
                        className="border-input bg-background placeholder:text-muted-foreground min-h-[80px] w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder={dialogConfig?.placeholder}
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        autoFocus
                    />
                    {noteDialog.open && noteDialog.action === 'cancel' && (
                        <RefundSection
                            value={cancelRefund}
                            onChange={(v) => {
                                setCancelRefund(v)
                                setCancelRefundErrors({})
                            }}
                            maxRefundable={cancelRefundable}
                            canRefund={hasPermission('customers.refund')}
                            errors={cancelRefundErrors}
                        />
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={closeNoteDialog}>
                            {m.common_close()}
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={noteText.trim().length < 3 || isPending}
                            onClick={submitNoteDialog}
                        >
                            {m.common_confirm()}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delivery settlement: how the customer paid on delivery */}
            <DeliverySettlementDialog
                open={settlementOpen}
                onOpenChange={setSettlementOpen}
                order={order ?? undefined}
                isPending={isPending}
                onConfirm={submitSettlement}
                entryPoint="order_detail"
            />
        </>
    )

    return { items: actionItems, dialogs }
}

/**
 * Hàng nút ngang — bố cục mặc định (desktop và mọi call-site cũ).
 *
 * Màn chi tiết đơn trên mobile không dùng component này: nó gọi thẳng
 * `useOrderWorkflowActions` để gộp chung vào menu kebab ở header.
 */
export function OrderActions(props: Props) {
    const { items, dialogs } = useOrderWorkflowActions(props)

    return (
        <>
            <div className="flex flex-wrap items-center gap-2">
                {items.map((item) => (
                    <Button
                        key={item.key}
                        size="sm"
                        variant={item.variant}
                        disabled={item.disabled}
                        aria-disabled={item.muted || undefined}
                        className={item.muted ? 'opacity-50' : undefined}
                        title={item.title}
                        onClick={item.onSelect}
                    >
                        {item.icon}
                        {item.label}
                    </Button>
                ))}
            </div>
            {dialogs}
        </>
    )
}
